<template>
    <label
        v-if="props.label"
        :for="inputId"
        class="form-label">
        {{ props.label }}
    </label>

    <template v-if="useNative">
        <input
            v-bind="attrs"
            :id="inputId"
            :name="inputName"
            :type="type"
            class="form-control"
            v-model="rawValue"
            @change="onNativeChange"
            :class="{'is-invalid': !!error}"
            :max="formatDate(props.maxDate)"
            :min="formatDate(props.minDate)"
            :required="props.required"
            :disabled="props.disabled"
            :aria-invalid="!!error ? 'true' : undefined"
            :aria-describedby="describedBy"
            :aria-label="ariaLabel" />
    </template>

    <div v-else ref="datepickerWrapper">
        <Datepicker
            v-bind="attrs"
            :format="dateFormat"
            v-model="model"
            :clearable="false"
            :month-picker="props.monthPicker"
            :auto-apply="props.autoApply"
            :utc="true"
            :class="{'is-invalid': !!error}"
            :enable-time-picker="props.enableTimePicker"
            :enable-minutes="props.enableMinutes"
            :enable-seconds="props.enableSeconds"
            :input-class-name="!!error ? 'form-control is-invalid' : 'form-control'"
            :ui="{input: !!error ? 'form-control is-invalid' : 'form-control'}"
            :max-date="props.maxDate"
            :min-date="props.minDate"
            :auto-position="props.autoPosition"
            :disabled="props.disabled"
            :required="props.required">
        </Datepicker>
    </div>
    <span
        :id="errorId"
        class="invalid-feedback"
        role="alert"
        v-if="error">
        {{ error }}
    </span>
</template>
<script setup lang="ts">
import {convertToUTC} from "@/utils";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import {computed, nextTick, onMounted, ref, useAttrs, watch} from "vue";
const attrs = useAttrs();

defineOptions({
    inheritAttrs: false,
});
const props = defineProps({
    error: {
        type: String,
        default: null,
    },
    errorId: {
        type: String,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    name: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    ariaLabel: {
        type: String,
        default: null,
    },
    ariaDescribedby: {
        type: String,
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
    monthPicker: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    autoApply: {
        type: Boolean,
        default: true,
    },
    enableTimePicker: {
        type: Boolean,
        default: false,
    },
    enableMinutes: {
        type: Boolean,
        default: true,
    },
    enableSeconds: {
        type: Boolean,
        default: false,
    },
    maxDate: {
        type: Date,
        default: undefined,
    },
    autoPosition: {
        type: Boolean,
        default: true,
    },
    minDate: {
        type: Date,
        default: undefined,
    },
    useNative: {
        type: Boolean,
        default: true,
    },
});

const type = computed(() => {
    if (props.enableTimePicker) {
        return "datetime-local";
    }
    if (props.monthPicker) {
        return "month";
    }
    return "date";
});

const emit = defineEmits(["update:modelValue"]);
const model = defineModel<Date>();
const datepickerWrapper = ref<HTMLElement | null>(null);

const isDate = (val: unknown): val is Date => {
    return val instanceof Date && !isNaN(val.getTime());
};

function parseDateValue(input: Date | string | undefined): Date | null {
    if (!input) {
        return null;
    }

    if (isDate(input)) {
        return input;
    }

    const value = String(input).trim();
    if (!value) {
        return null;
    }

    const [datePart, timePart] = value.split(" ");
    const dateParts = datePart.split("-").map(Number);

    if (dateParts.length === 3 && dateParts.every((part) => !isNaN(part))) {
        const [year, month, day] = dateParts;
        if (timePart) {
            const [hours = "00", minutes = "00", seconds = "00"] = timePart.split(":");
            const date = new Date(year, month - 1, day, Number(hours), Number(minutes), Number(seconds));
            if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day
                || date.getHours() !== Number(hours) || date.getMinutes() !== Number(minutes) || date.getSeconds() !== Number(seconds)) {
                return null;
            }
            return date;
        }
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return null;
        }
        return date;
    }

    if (dateParts.length === 2 && dateParts.every((part) => !isNaN(part))) {
        const [year, month] = dateParts;
        const date = new Date(year, month - 1, 1);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1) {
            return null;
        }
        return date;
    }

    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatDate(input: Date | string | undefined) {
    const date = parseDateValue(input);
    if (!date) {
        return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    if (props.monthPicker) {
        return `${year}-${month}`;
    }

    if (props.enableTimePicker) {
        const HH = String(date.getHours()).padStart(2, "0");
        const mm = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day} ${HH}:${mm}`;
    }

    return `${year}-${month}-${day}`;
}
const rawValue = ref(formatDate(model.value));

watch(() => model.value, (newValue) => {
    const formatted = formatDate(newValue);
    if (formatted !== formatDate(rawValue.value)) {
        rawValue.value = formatted;
    }
});

function onNativeChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (!parseDateValue(value)) {
        // Invalid date: leave the input as-is so the user can fix it, don't touch the model
        emit("update:modelValue", null);
        return;
    }

    // When the user picks/enters a valid date, emit it back to the parent
    const result = props.monthPicker ? convertToUTC(new Date(`${value}-01`)) : value;
    emit("update:modelValue", result);
}

const dateFormat = computed(() => {
    let format = "yyyy-MM-dd";

    if (props.monthPicker) {
        format = "MM-yyyy";
    }

    if (props.enableTimePicker) {
        if (props.enableSeconds) {
            format = "yyyy-MM-dd HH:mm:ss";
        } else {
            format = "yyyy-MM-dd HH:mm";
        }
    }
    return format;
});

const inputId = computed(() => props.id || props.name || "date-picker");
const inputName = computed(() => props.name || props.id || undefined);
const ariaLabel = computed(() => props.ariaLabel || props.label || undefined);
const describedBy = computed(() => {
    const ids = [props.ariaDescribedby, props.error ? props.errorId : null].filter(Boolean) as string[];
    return ids.length ? ids.join(" ") : undefined;
});

function setDatepickerInputAttributes() {
    if (props.useNative || !datepickerWrapper.value) {
        return;
    }

    const input = datepickerWrapper.value.querySelector<HTMLInputElement>("input");
    if (!input) {
        return;
    }

    input.id = inputId.value;
    input.name = inputName.value || "";
    input.setAttribute("aria-label", ariaLabel.value || "");
    input.setAttribute("aria-invalid", !!props.error ? "true" : "false");
    input.setAttribute("aria-describedby", describedBy.value || "");
    if (props.required) {
        input.setAttribute("required", "required");
    } else {
        input.removeAttribute("required");
    }

    if (props.disabled) {
        input.setAttribute("disabled", "disabled");
    } else {
        input.removeAttribute("disabled");
    }
}

onMounted(() => {
    nextTick(() => setDatepickerInputAttributes());
});

watch(
    [() => props.error, () => props.disabled, () => props.required, () => props.ariaLabel, () => props.label, () => props.ariaDescribedby, () => props.useNative],
    () => {
        nextTick(() => setDatepickerInputAttributes());
    }
);
</script>
